(function () {
  const money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

  function cents(value) {
    return money.format((Number(value) || 0) / 100);
  }

  function parseJson(root) {
    const node = root.querySelector('[data-metzger-selector-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[Metzgerpaket] Could not parse selector JSON', error);
      return null;
    }
  }

  function showToast(title, message) {
    document.querySelectorAll('.r-metzger-toast').forEach((toast) => toast.remove());
    const toast = document.createElement('div');
    toast.className = 'r-metzger-toast';
    toast.innerHTML = `<strong>${title}</strong><p>${message}</p>`;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 220);
    }, 5200);
  }

  function intervalPriority(plan) {
    const text = `${plan.name || ''} ${plan.description || ''}`.toLowerCase();
    if (text.includes('4') || text.includes('monat')) return 0;
    if (text.includes('8')) return 1;
    if (text.includes('12')) return 2;
    return 5;
  }

  class MetzgerSelector {
    constructor(root) {
      if (root.dataset.metzgerSelectorBound === 'true') return;
      this.root = root;
      this.data = parseJson(root);
      if (!this.data) return;
      root.dataset.metzgerSelectorBound = 'true';

      this.packageButtons = Array.from(root.querySelectorAll('[data-package-id]'));
      this.sizeTarget = root.querySelector('[data-size-options]');
      this.intervalTarget = root.querySelector('[data-interval-options]');
      this.totalTarget = root.querySelector('[data-total-price]');
      this.portionTarget = root.querySelector('[data-portion-price]');
      this.savingsSummaryTarget = root.querySelector('[data-savings-summary]');
      this.deliveryTarget = root.querySelector('[data-next-delivery]');
      this.stockTarget = root.querySelector('[data-stock-left]');
      this.addButton = root.querySelector('[data-add-to-cart]');
      this.cartDialog = root.querySelector('[data-metzger-cart-dialog]');
      this.cartDialogCancel = root.querySelector('[data-metzger-cart-cancel]');
      this.cartDialogConfirm = root.querySelector('[data-metzger-cart-confirm]');

      this.state = {
        packageId: this.packageButtons[0]?.dataset.packageId,
        variantId: null,
        sellingPlanId: null
      };

      const shippingLabel = document.querySelector('[data-countdown-dates]')?.textContent?.trim();
      this.deliveryTarget.textContent = shippingLabel ? `Voraussichtliches Versandfenster: ${shippingLabel}` : 'Erste Woche des nächsten Versandmonats';
      this.bind();
      this.selectPackage(this.state.packageId);
    }

    bind() {
      this.packageButtons.forEach((button) => {
        const trigger = button.querySelector('[data-package-pick]') || button;
        trigger.addEventListener('click', () => this.selectPackage(button.dataset.packageId));
        button.addEventListener('click', (event) => {
          if (event.target.closest('button, summary, a, input, select, textarea')) return;
          this.selectPackage(button.dataset.packageId);
        });
      });

      this.addButton.addEventListener('click', () => this.addToCart());
      this.cartDialogCancel?.addEventListener('click', () => this.cartDialog.close());
      this.cartDialogConfirm?.addEventListener('click', () => {
        this.cartDialog.close();
        this.addToCart({ allowCartClear: true });
      });
    }

    get selectedPackage() {
      return this.data.packages[this.state.packageId];
    }

    get selectedVariant() {
      return this.selectedPackage?.variants.find((variant) => String(variant.id) === String(this.state.variantId));
    }

    get selectedPlan() {
      return this.selectedVariant?.sellingPlans.find((plan) => String(plan.id) === String(this.state.sellingPlanId));
    }

    selectPackage(packageId) {
      this.state.packageId = packageId;
      this.packageButtons.forEach((button) => {
        const isSelected = button.dataset.packageId === packageId;
        button.classList.toggle('is-selected', isSelected);
        const trigger = button.querySelector('[data-package-pick]');
        if (trigger) trigger.setAttribute('aria-pressed', String(isSelected));
      });

      const firstVariant = this.selectedPackage?.variants.find((variant) => variant.available) || this.selectedPackage?.variants[0];
      this.state.variantId = firstVariant?.id || null;
      this.renderSizes();
      this.renderIntervals();
      this.updateSummary();
    }

    selectVariant(variantId) {
      const keepFocus = this.sizeTarget.contains(document.activeElement);
      this.state.variantId = variantId;
      this.renderSizes();
      this.renderIntervals();
      this.updateSummary();
      if (keepFocus) this.sizeTarget.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
    }

    selectPlan(planId) {
      const keepFocus = this.intervalTarget.contains(document.activeElement);
      this.state.sellingPlanId = planId;
      this.renderIntervals();
      this.updateSummary();
      if (keepFocus) this.intervalTarget.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
    }

    renderSizes() {
      const variants = this.selectedPackage?.variants || [];
      this.sizeTarget.innerHTML = '';

      variants.forEach((variant) => {
        const comparisonPrice = this.comparisonPriceForVariant(variant);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'r-metzger-selector__size size-option';
        button.dataset.variantId = String(variant.id);
        button.disabled = !variant.available;
        const isSelected = String(variant.id) === String(this.state.variantId);
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        button.innerHTML = `
          <span class="r-metzger-selector__option-row">
            <span class="r-metzger-selector__option-title">${variant.title}</span>
            <span class="r-metzger-selector__option-price">Einzelkaufwert ${cents(comparisonPrice)}</span>
          </span>
          <span class="j-metzger-size-weight">${this.weightLabel(variant.weight).replace(/ Gesamtgewicht$/, '')}<svg viewBox="0 0 90 48" aria-hidden="true" focusable="false">${Number(variant.weight) === 4400 ? '<path d="M3 15 21 6 39 15 21 24Z M3 15v20l18 9 18-9V15 M21 24v20 M51 15 69 6 87 15 69 24Z M51 15v20l18 9 18-9V15 M69 24v20"/>' : '<path d="M25 15 45 5 65 15 45 26Z M25 15v21l20 10 20-10V15 M45 26v20"/>'}</svg></span>
          ${variant.detail ? `<span class="r-small">${variant.detail}</span>` : ''}
        `;
        button.addEventListener('click', () => this.selectVariant(variant.id));
        this.sizeTarget.appendChild(button);
      });
    }

    renderIntervals() {
      const plans = [...(this.selectedVariant?.sellingPlans || [])].sort((a, b) => intervalPriority(a) - intervalPriority(b));
      if (!plans.length) {
        this.state.sellingPlanId = null;
        this.intervalTarget.innerHTML = '<p class="r-small">Für diese Variante ist aktuell kein Abo-Intervall aktiv.</p>';
        return;
      }

      if (!plans.some((plan) => String(plan.id) === String(this.state.sellingPlanId))) {
        this.state.sellingPlanId = plans[0].id;
      }

      this.intervalTarget.innerHTML = '';
      plans.forEach((plan) => {
        const savings = Math.max(0, this.comparisonPriceForVariant(this.selectedVariant) - (Number(plan.price) || 0));
        const context = savings ? `Du sparst ${cents(savings)} pro Lieferung` : 'Abo-Preis pro Lieferung';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'r-metzger-selector__interval';
        button.dataset.planId = String(plan.id);
        const isSelected = String(plan.id) === String(this.state.sellingPlanId);
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        const planLabel = /^\s*\d+\s+Monate?\s*$/i.test(plan.name || '') ? `Alle ${plan.name}` : plan.name;
        button.innerHTML = `
          <span class="r-metzger-selector__option-row">
            <span class="r-metzger-selector__option-title">${planLabel}</span>
            <span class="r-metzger-selector__option-price">${cents(plan.price)}</span>
          </span>
          <span class="r-small">${context}</span>
        `;
        button.addEventListener('click', () => this.selectPlan(plan.id));
        this.intervalTarget.appendChild(button);
      });
    }

    updateSummary() {
      const variant = this.selectedVariant;
      const plan = this.selectedPlan;
      const price = plan?.price || variant?.price || 0;
      const grams = Number(variant?.weight) || this.weightFromTitle(variant?.title);
      const portionSize = Number(this.data.portionSize) || 150;
      const portionCount = grams ? grams / portionSize : 0;
      const portionPrice = portionCount ? price / portionCount : 0;

      this.totalTarget.textContent = `${cents(price)} je Lieferung`;
      const recap = this.root.querySelector('[data-subscription-recap]');
      if (recap) recap.textContent = plan ? `${variant.title} · ${plan.name} · wiederkehrende Lieferung` : 'Wähle Größe und Lieferrhythmus.';
      this.portionTarget.textContent = portionPrice ? `ca. ${cents(portionPrice)} pro Portion` : 'Portionspreis nach Gewicht';
      if (this.savingsSummaryTarget) {
        this.savingsSummaryTarget.textContent = this.annualSavingsLabel(variant, plan);
      }
      this.stockTarget.textContent = variant?.available ? 'Pausierbar, überspringbar oder kündbar' : 'Aktuell nicht verfügbar';
      const isReady = Boolean(variant && variant.available && plan);
      this.addButton.disabled = !isReady;
      this.addButton.setAttribute('aria-disabled', String(!isReady));
      this.addButton.setAttribute('aria-busy', 'false');
      this.addButton.textContent = isReady ? 'Metzgerpaket-Abo starten' : 'Auswahl noch nicht verfügbar';
      // Presentation observers receive the native selection; cart and price calculation stay here.
      this.root.dataset.previewPackage = this.state.packageId || '';
      this.root.dataset.previewWeight = String(grams);
      this.root.dispatchEvent(new CustomEvent('metzger:selectionchange', { bubbles: true }));
    }

    weightLabel(weight) {
      const grams = Number(weight) || 0;
      if (!grams) return 'Monatlich kuratierte Auswahl';
      if (grams >= 1000) return `${String(grams / 1000).replace('.', ',')} kg Gesamtgewicht`;
      return `${grams} g Gesamtgewicht`;
    }

    weightFromTitle(title = '') {
      const match = title.match(/(\d+(?:[,.]\d+)?)\s*kg/i);
      return match ? Number(match[1].replace(',', '.')) * 1000 : 0;
    }

    sizeSavingsLabel(variant) {
      const amount = this.savingsAmountForVariant(variant);
      if (!amount) return '';
      return `Spare bis zu ${money.format(amount)} gegenüber Einzelkauf`;
    }

    comparisonPriceForVariant(variant) {
      return Number(variant?.price) || 0;
    }

    savingsAmountForVariant(variant) {
      if (!variant) return 0;
      const comparisonPrice = this.comparisonPriceForVariant(variant);
      const planPrices = (variant.sellingPlans || []).map((plan) => Number(plan.price) || comparisonPrice);
      const lowestPlanPrice = planPrices.length ? Math.min(...planPrices) : comparisonPrice;
      return Math.max(0, comparisonPrice - lowestPlanPrice) / 100;
    }

    deliveriesPerYear(plan) {
      const text = `${plan?.name || ''} ${plan?.description || ''}`.toLowerCase();
      if (text.includes('viertel') || text.includes('12')) return 4;
      if (text.includes('2 monat') || text.includes('8')) return 6;
      if (text.includes('monat') || text.includes('4')) return 12;
      return 12;
    }

    annualSavingsLabel(variant, plan) {
      const perDelivery = Math.max(0, this.comparisonPriceForVariant(variant) - (Number(plan?.price) || 0)) / 100;
      if (!perDelivery || !plan) return '';
      const annualSavings = perDelivery * this.deliveriesPerYear(plan);
      const prefix = this.data.savings?.annualPrefix || 'Im Jahr';
      return `Spart ${money.format(perDelivery)} je Lieferung. ${prefix} bis zu ${money.format(annualSavings)}.`;
    }

    async addToCart({ allowCartClear = false } = {}) {
      const variant = this.selectedVariant;
      const plan = this.selectedPlan;
      if (!variant || !plan) {
        showToast('Abo noch nicht komplett', 'Bitte wähle Paket, Größe und Lieferintervall.');
        return;
      }

      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-disabled', 'true');
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.textContent = 'Wird vorbereitet...';

      let cartBefore = null;
      let addedKey = null;

      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) throw new Error('Cart could not be loaded');
        cartBefore = await cartResponse.json();

        if (cartBefore.item_count > 0 && !allowCartClear) {
          this.addButton.disabled = false;
          this.addButton.setAttribute('aria-disabled', 'false');
          this.addButton.setAttribute('aria-busy', 'false');
          this.addButton.textContent = 'Metzgerpaket-Abo starten';
          if (this.cartDialog?.showModal) {
            this.cartDialog.showModal();
          } else {
            showToast('Metzgerpaket einzeln bestellen', this.data.cartRestrictionMessage);
          }
          return;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Metzger-Selector': '1'
          },
          body: JSON.stringify({
            items: [{
              id: variant.id,
              quantity: 1,
              selling_plan: plan.id,
              properties: {
                '_Paket': this.selectedPackage.title,
                '_Lieferintervall': plan.name
              }
            }]
          })
        });

        const addPayload = await response.json();
        if (!response.ok) throw new Error(addPayload.description || 'Cart add failed');
        addedKey = addPayload.items?.[0]?.key || addPayload.key || null;

        if (cartBefore.item_count > 0 && allowCartClear) {
          const cartAfterAddResponse = await fetch('/cart.js');
          if (!cartAfterAddResponse.ok) throw new Error('Updated cart could not be loaded');
          const cartAfterAdd = await cartAfterAddResponse.json();
          const addedItem = addedKey
            ? cartAfterAdd.items.find((item) => item.key === addedKey)
            : cartAfterAdd.items.find((item) => Number(item.variant_id) === Number(variant.id));

          if (!addedItem || !addedItem.selling_plan_allocation) {
            throw new Error('Subscription item missing after add');
          }
          addedKey = addedItem.key;

          const updates = {};
          cartAfterAdd.items.forEach((item) => {
            updates[item.key] = item.key === addedKey ? 1 : 0;
          });

          const cleanupResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
          if (!cleanupResponse.ok) throw new Error('Existing cart could not be replaced safely');
          const cleanedCart = await cleanupResponse.json();
          const retainedItem = cleanedCart.items?.find((item) => item.key === addedKey);
          if (cleanedCart.item_count !== 1 || !retainedItem?.selling_plan_allocation) {
            throw new Error('Cart replacement could not be verified');
          }
        }

        window.location.href = '/checkout';
      } catch (error) {
        console.error('[Metzgerpaket] Add to cart failed', error);
        if (cartBefore && addedKey) {
          try {
            const currentResponse = await fetch('/cart.js');
            if (!currentResponse.ok) throw new Error('Rollback cart could not be loaded');
            const currentCart = await currentResponse.json();
            const originalKeysStillExist = cartBefore.items.every((originalItem) =>
              currentCart.items.some((currentItem) => currentItem.key === originalItem.key)
            );

            if (originalKeysStillExist) {
              const restoreUpdates = {};
              cartBefore.items.forEach((item) => {
                restoreUpdates[item.key] = item.quantity;
              });
              if (!cartBefore.items.some((item) => item.key === addedKey)) restoreUpdates[addedKey] = 0;
              const restoreResponse = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: restoreUpdates })
              });
              if (!restoreResponse.ok) throw new Error('Rollback update failed');
            } else {
              const clearResponse = await fetch('/cart/clear.js', { method: 'POST' });
              if (!clearResponse.ok) throw new Error('Rollback clear failed');

              if (cartBefore.items.length > 0) {
                const restoreItems = cartBefore.items.map((item) => {
                  const restoredItem = {
                    id: item.variant_id,
                    quantity: item.quantity,
                    properties: item.properties || {}
                  };
                  const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
                  if (sellingPlanId) restoredItem.selling_plan = sellingPlanId;
                  return restoredItem;
                });
                const addBackResponse = await fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ items: restoreItems })
                });
                if (!addBackResponse.ok) throw new Error('Rollback add failed');
              }
            }
          } catch (rollbackError) {
            console.error('[Metzgerpaket] Cart rollback failed', rollbackError);
            showToast('Warenkorb bitte prüfen', 'Die Wiederherstellung konnte nicht bestätigt werden. Bitte öffne den Warenkorb, bevor du erneut bestellst.');
            this.addButton.disabled = false;
            this.addButton.setAttribute('aria-disabled', 'false');
            this.addButton.setAttribute('aria-busy', 'false');
            this.addButton.textContent = 'Metzgerpaket-Abo starten';
            return;
          }
        }
        showToast('Das hat nicht geklappt', 'Bitte probiere es noch einmal oder melde dich direkt bei uns.');
        this.addButton.disabled = false;
        this.addButton.setAttribute('aria-disabled', 'false');
        this.addButton.setAttribute('aria-busy', 'false');
        this.addButton.textContent = 'Metzgerpaket-Abo starten';
      }
    }
  }

  function start(scope = document) {
    const roots = scope.matches?.('[data-metzger-selector]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-metzger-selector]') || []);
    roots.forEach((root) => new MetzgerSelector(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start());
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();
